import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
    ChevronLeft, Share2, Eye, Users, Clock, Download, Plus, Trash2, 
    GripVertical, FileText, Video, Link as LinkIcon, Image, Settings as SettingsIcon,
    BarChart2, Layers, Copy, Mail, UserPlus, Building2, ExternalLink, Type
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import RichTextEditor from '../components/RichTextEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatTime(seconds) {
    if (!seconds) return '0s';
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}

function formatFileSize(bytes) {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

const CONTENT_TYPE_ICONS = {
    document: FileText,
    video: Video,
    link: LinkIcon,
    image: Image,
    richtext: Type,
};

// Content & Structure Tab
function ContentStructureTab({ room, sections, onSectionCreate, onContentCreate, onSectionDelete, onContentDelete, onRefresh }) {
    const [selectedSection, setSelectedSection] = useState(null);
    const [showAddSection, setShowAddSection] = useState(false);
    const [showAddContent, setShowAddContent] = useState(false);
    const [sectionForm, setSectionForm] = useState({ name: '', description: '' });
    const [contentForm, setContentForm] = useState({ title: '', description: '', content_type: 'document', file: null, rich_content: '' });

    useEffect(() => {
        if (sections.length > 0 && !selectedSection) {
            setSelectedSection(sections[0]);
        }
    }, [sections]);

    const handleCreateSection = async () => {
        if (!sectionForm.name.trim()) return;
        await onSectionCreate(sectionForm);
        setSectionForm({ name: '', description: '' });
        setShowAddSection(false);
    };

    const handleCreateContent = async () => {
        if (!contentForm.title.trim()) return;
        
        let fileData = null;
        let fileName = null;
        let fileType = null;
        let fileSize = 0;
        
        // Handle richtext content type
        if (contentForm.content_type === 'richtext') {
            await onContentCreate(selectedSection.section_id, {
                ...contentForm,
                rich_content: contentForm.rich_content,
            });
            setContentForm({ title: '', description: '', content_type: 'document', file: null, rich_content: '' });
            setShowAddContent(false);
            return;
        }
        
        if (contentForm.file) {
            fileSize = contentForm.file.size;
            fileName = contentForm.file.name;
            fileType = contentForm.file.name.split('.').pop().toLowerCase();
            
            const reader = new FileReader();
            reader.onload = async (e) => {
                fileData = e.target.result.split(',')[1];
                await onContentCreate(selectedSection.section_id, {
                    ...contentForm,
                    file_name: fileName,
                    file_type: fileType,
                    file_size: fileSize,
                    file_data: fileData,
                });
                setContentForm({ title: '', description: '', content_type: 'document', file: null, rich_content: '' });
                setShowAddContent(false);
            };
            reader.readAsDataURL(contentForm.file);
        } else {
            await onContentCreate(selectedSection.section_id, contentForm);
            setContentForm({ title: '', description: '', content_type: 'document', file: null, rich_content: '' });
            setShowAddContent(false);
        }
    };

    return (
        <div className="grid grid-cols-12 gap-6">
            {/* Sections Sidebar */}
            <div className="col-span-3">
                <div className="bg-white rounded-xl border border-stone-200 p-4">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold text-stone-900">Sections</h3>
                        <Button size="sm" variant="ghost" onClick={() => setShowAddSection(true)}>
                            <Plus className="w-4 h-4" />
                        </Button>
                    </div>
                    
                    {sections.length === 0 ? (
                        <p className="text-sm text-stone-400 text-center py-4">No sections yet</p>
                    ) : (
                        <div className="space-y-2">
                            {sections.map(section => (
                                <button
                                    key={section.section_id}
                                    onClick={() => setSelectedSection(section)}
                                    className={`w-full text-left p-3 rounded-lg border transition-colors flex items-start gap-2 ${
                                        selectedSection?.section_id === section.section_id
                                            ? 'border-stone-300 bg-stone-100'
                                            : 'border-stone-100 hover:border-stone-200'
                                    }`}
                                >
                                    <GripVertical className="w-4 h-4 text-stone-300 mt-0.5 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <p className={`text-sm font-medium truncate ${
                                            selectedSection?.section_id === section.section_id ? 'text-stone-900' : 'text-stone-700'
                                        }`}>
                                            {section.name}
                                        </p>
                                        <p className="text-xs text-stone-400">{section.items?.length || section.item_count || 0} items</p>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Content Area */}
            <div className="col-span-9">
                {selectedSection ? (
                    <div className="bg-white rounded-xl border border-stone-200 p-6">
                        <div className="flex items-start justify-between mb-4">
                            <div>
                                <h3 className="text-lg font-semibold text-stone-900">{selectedSection.name}</h3>
                                {selectedSection.description && (
                                    <p className="text-sm text-stone-500 mt-1">{selectedSection.description}</p>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                <Button onClick={() => setShowAddContent(true)} className="bg-stone-900 hover:bg-stone-800">
                                    <Plus className="w-4 h-4 mr-1" />
                                    Add Content Item
                                </Button>
                                <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => onSectionDelete(selectedSection.section_id)}
                                    className="text-stone-400 hover:text-red-500"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>

                        {(!selectedSection.items || selectedSection.items.length === 0) ? (
                            <div className="border-2 border-dashed border-stone-200 rounded-xl p-12 text-center">
                                <FileText className="w-12 h-12 mx-auto text-stone-300 mb-4" />
                                <p className="text-stone-500 mb-2">No content items yet</p>
                                <Button onClick={() => setShowAddContent(true)} variant="outline">
                                    Add your first content item
                                </Button>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {selectedSection.items.map(item => {
                                    const IconComponent = CONTENT_TYPE_ICONS[item.content_type] || FileText;
                                    return (
                                        <div key={item.item_id} className="flex items-center gap-4 p-4 border border-stone-100 rounded-lg hover:border-stone-200 group">
                                            <GripVertical className="w-4 h-4 text-stone-300 cursor-move" />
                                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                                item.content_type === 'video' ? 'bg-violet-50' :
                                                item.content_type === 'link' ? 'bg-blue-50' :
                                                item.content_type === 'image' ? 'bg-amber-50' :
                                                item.content_type === 'richtext' ? 'bg-emerald-50' : 'bg-stone-100'
                                            }`}>
                                                <IconComponent className={`w-5 h-5 ${
                                                    item.content_type === 'video' ? 'text-violet-600' :
                                                    item.content_type === 'link' ? 'text-blue-600' :
                                                    item.content_type === 'image' ? 'text-amber-600' :
                                                    item.content_type === 'richtext' ? 'text-emerald-600' : 'text-stone-700'
                                                }`} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <p className="text-sm font-medium text-stone-800">{item.title}</p>
                                                    {item.content_type === 'richtext' && (
                                                        <span className="text-xs px-2 py-0.5 bg-emerald-50 text-emerald-600 rounded-full">Rich Text</span>
                                                    )}
                                                </div>
                                                {item.description && (
                                                    <p className="text-xs text-stone-500 mt-0.5 truncate">{item.description}</p>
                                                )}
                                                <div className="flex items-center gap-3 mt-1 text-xs text-stone-400">
                                                    {item.file_name && <span>{item.file_name}</span>}
                                                    {item.file_size > 0 && <span>{formatFileSize(item.file_size)}</span>}
                                                    <span><Eye className="w-3 h-3 inline mr-1" />{item.views || 0} views</span>
                                                    {item.content_type !== 'richtext' && (
                                                        <span><Download className="w-3 h-3 inline mr-1" />{item.downloads || 0} downloads</span>
                                                    )}
                                                </div>
                                            </div>
                                            <Button 
                                                variant="ghost" 
                                                size="sm"
                                                onClick={() => onContentDelete(item.item_id)}
                                                className="opacity-0 group-hover:opacity-100 text-stone-400 hover:text-red-500"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="bg-white rounded-xl border border-stone-200 border-dashed p-12 text-center">
                        <Layers className="w-12 h-12 mx-auto text-stone-300 mb-4" />
                        <h3 className="text-lg font-semibold text-stone-700 mb-2">Select a Section</h3>
                        <p className="text-stone-500">Choose a section from the sidebar to manage its content</p>
                    </div>
                )}
            </div>

            {/* Add Section Modal */}
            <Dialog open={showAddSection} onOpenChange={setShowAddSection}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add Section</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div>
                            <Label>Section Name *</Label>
                            <Input
                                value={sectionForm.name}
                                onChange={(e) => setSectionForm(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="e.g., Getting Started"
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <Label>Description (optional)</Label>
                            <Textarea
                                value={sectionForm.description}
                                onChange={(e) => setSectionForm(prev => ({ ...prev, description: e.target.value }))}
                                placeholder="Brief description of this section"
                                className="mt-1"
                                rows={2}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAddSection(false)}>Cancel</Button>
                        <Button onClick={handleCreateSection} className="bg-stone-900 hover:bg-stone-800">Add Section</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add Content Modal */}
            <Dialog open={showAddContent} onOpenChange={setShowAddContent}>
                <DialogContent className={contentForm.content_type === 'richtext' ? 'sm:max-w-3xl' : ''}>
                    <DialogHeader>
                        <DialogTitle>Add Content Item</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div>
                            <Label>Title *</Label>
                            <Input
                                value={contentForm.title}
                                onChange={(e) => setContentForm(prev => ({ ...prev, title: e.target.value }))}
                                placeholder="e.g., AI Transformation Overview"
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <Label>Description (optional)</Label>
                            <Textarea
                                value={contentForm.description}
                                onChange={(e) => setContentForm(prev => ({ ...prev, description: e.target.value }))}
                                className="mt-1"
                                rows={2}
                            />
                        </div>
                        <div>
                            <Label>Content Type</Label>
                            <Select
                                value={contentForm.content_type}
                                onValueChange={(v) => setContentForm(prev => ({ ...prev, content_type: v, rich_content: '' }))}
                            >
                                <SelectTrigger className="mt-1">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="document">Document</SelectItem>
                                    <SelectItem value="video">Video</SelectItem>
                                    <SelectItem value="image">Image</SelectItem>
                                    <SelectItem value="link">External Link</SelectItem>
                                    <SelectItem value="richtext">Rich Text Content</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        {contentForm.content_type === 'richtext' ? (
                            <div>
                                <Label>Content</Label>
                                <div className="mt-1">
                                    <RichTextEditor
                                        content={contentForm.rich_content}
                                        onChange={(html) => setContentForm(prev => ({ ...prev, rich_content: html }))}
                                        placeholder="Write your content here..."
                                        minHeight="250px"
                                        showContentLibrary={true}
                                    />
                                </div>
                            </div>
                        ) : contentForm.content_type === 'link' ? (
                            <div>
                                <Label>External URL</Label>
                                <Input
                                    value={contentForm.external_url || ''}
                                    onChange={(e) => setContentForm(prev => ({ ...prev, external_url: e.target.value }))}
                                    placeholder="https://..."
                                    className="mt-1"
                                />
                            </div>
                        ) : (
                            <div>
                                <Label>File</Label>
                                <Input
                                    type="file"
                                    onChange={(e) => setContentForm(prev => ({ ...prev, file: e.target.files[0] }))}
                                    className="mt-1"
                                />
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAddContent(false)}>Cancel</Button>
                        <Button onClick={handleCreateContent} className="bg-stone-900 hover:bg-stone-800">Add Content</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// Analytics Tab
function AnalyticsTab({ room, invitees }) {
    return (
        <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-4 gap-4">
                <div className="bg-white rounded-xl border border-stone-200 p-5">
                    <div className="flex items-center gap-2 mb-1">
                        <Eye className="w-4 h-4 text-stone-400" />
                        <p className="text-xs text-stone-400">Total Views</p>
                    </div>
                    <p className="text-2xl font-bold text-stone-900">{room.total_views || 0}</p>
                    <p className="text-xs text-stone-400 mt-1">Across all content</p>
                </div>
                <div className="bg-white rounded-xl border border-stone-200 p-5">
                    <div className="flex items-center gap-2 mb-1">
                        <Users className="w-4 h-4 text-stone-600" />
                        <p className="text-xs text-stone-400">Unique Visitors</p>
                    </div>
                    <p className="text-2xl font-bold text-stone-900">{invitees.filter(i => i.first_viewed_at).length}</p>
                    <p className="text-xs text-stone-400 mt-1">Individual contacts</p>
                </div>
                <div className="bg-white rounded-xl border border-stone-200 p-5">
                    <div className="flex items-center gap-2 mb-1">
                        <Clock className="w-4 h-4 text-amber-500" />
                        <p className="text-xs text-stone-400">Avg. Time Spent</p>
                    </div>
                    <p className="text-2xl font-bold text-stone-900">{formatTime(room.avg_time_spent)}</p>
                    <p className="text-xs text-stone-400 mt-1">Per visitor</p>
                </div>
                <div className="bg-white rounded-xl border border-stone-200 p-5">
                    <div className="flex items-center gap-2 mb-1">
                        <Download className="w-4 h-4 text-emerald-500" />
                        <p className="text-xs text-stone-400">Total Downloads</p>
                    </div>
                    <p className="text-2xl font-bold text-stone-900">{room.total_downloads || 0}</p>
                    <p className="text-xs text-stone-400 mt-1">Files downloaded</p>
                </div>
            </div>

            {/* Visitor Activity */}
            <div className="bg-white rounded-xl border border-stone-200 p-6">
                <h3 className="font-semibold text-stone-900 mb-1">Visitor Activity</h3>
                <p className="text-sm text-stone-500 mb-4">Detailed engagement by contact</p>
                
                {invitees.length === 0 ? (
                    <div className="text-center py-8 text-stone-400">
                        <Users className="w-10 h-10 mx-auto mb-3 opacity-40" />
                        <p>No visitors yet</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {invitees.map(invitee => (
                            <div key={invitee.invitee_id} className="border border-stone-100 rounded-xl p-4">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center text-sm font-semibold text-stone-900">
                                            {invitee.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?'}
                                        </div>
                                        <div>
                                            <p className="font-medium text-stone-900">{invitee.name}</p>
                                            <p className="text-sm text-stone-500">{invitee.email}</p>
                                        </div>
                                    </div>
                                    <span className={`text-xs px-2 py-1 rounded-full ${
                                        invitee.status === 'active' ? 'bg-emerald-50 text-emerald-700' :
                                        invitee.status === 'sent' ? 'bg-blue-50 text-blue-700' :
                                        'bg-stone-100 text-stone-600'
                                    }`}>
                                        {invitee.status === 'active' ? 'Active' : 
                                         invitee.status === 'sent' ? 'Invited' : 'Pending'}
                                    </span>
                                </div>
                                
                                <div className="grid grid-cols-4 gap-4">
                                    <div className="bg-stone-50 rounded-lg p-3">
                                        <p className="text-xs text-stone-400">Total Views</p>
                                        <p className="text-lg font-semibold text-stone-900">{invitee.total_views || 0}</p>
                                    </div>
                                    <div className="bg-stone-50 rounded-lg p-3">
                                        <p className="text-xs text-stone-400">Time Spent</p>
                                        <p className="text-lg font-semibold text-stone-900">{formatTime(invitee.total_time_spent)}</p>
                                    </div>
                                    <div className="bg-stone-50 rounded-lg p-3">
                                        <p className="text-xs text-stone-400">Items Viewed</p>
                                        <p className="text-lg font-semibold text-stone-900">{invitee.items_viewed || 0}</p>
                                    </div>
                                    <div className="bg-stone-50 rounded-lg p-3">
                                        <p className="text-xs text-stone-400">Downloads</p>
                                        <p className="text-lg font-semibold text-stone-900">{invitee.downloads || 0}</p>
                                    </div>
                                </div>
                                
                                <div className="flex items-center justify-between mt-3 pt-3 border-t border-stone-100 text-xs text-stone-400">
                                    <span>First viewed: {invitee.first_viewed_at ? new Date(invitee.first_viewed_at).toLocaleString() : 'Never'}</span>
                                    <span>Last viewed: {invitee.last_viewed_at ? new Date(invitee.last_viewed_at).toLocaleString() : 'Never'}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

// Settings Tab
function SettingsTab({ room, onUpdate }) {
    const [form, setForm] = useState({
        name: room.name || '',
        status: room.status || 'draft',
        expiration_date: room.expiration_date || '',
        theme: room.theme || 'default',
        allow_client_invites: room.allow_client_invites || false,
        restrict_to_domain: room.restrict_to_domain || false,
        allowed_domain: room.allowed_domain || '',
    });
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        setSaving(true);
        try {
            await onUpdate(form);
            toast.success('Settings saved');
        } finally {
            setSaving(false);
        }
    };

    const copyLink = () => {
        const baseUrl = window.location.origin;
        const link = `${baseUrl}${room.access_link}`;
        navigator.clipboard.writeText(link);
        toast.success('Link copied to clipboard');
    };

    return (
        <div className="max-w-xl">
            <div className="bg-white rounded-xl border border-stone-200 p-6">
                <h3 className="font-semibold text-stone-900 mb-4">Deal Room Settings</h3>
                
                <div className="space-y-4">
                    <div>
                        <Label>Room Name</Label>
                        <Input
                            value={form.name}
                            onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                            className="mt-1"
                        />
                    </div>
                    
                    <div>
                        <Label>Access Link</Label>
                        <div className="flex items-center gap-2 mt-1">
                            <Input value={`${window.location.origin}${room.access_link}`} readOnly className="bg-stone-50" />
                            <Button variant="outline" size="sm" onClick={copyLink}>
                                <Copy className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                    
                    <div>
                        <Label>Expiration Date</Label>
                        <Input
                            type="date"
                            value={form.expiration_date}
                            onChange={(e) => setForm(prev => ({ ...prev, expiration_date: e.target.value }))}
                            className="mt-1"
                        />
                    </div>
                    
                    <div>
                        <Label>Theme</Label>
                        <Select value={form.theme} onValueChange={(v) => setForm(prev => ({ ...prev, theme: v }))}>
                            <SelectTrigger className="mt-1">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="default">Default</SelectItem>
                                <SelectItem value="dark">Dark</SelectItem>
                                <SelectItem value="minimal">Minimal</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    
                    <div>
                        <Label>Status</Label>
                        <Select value={form.status} onValueChange={(v) => setForm(prev => ({ ...prev, status: v }))}>
                            <SelectTrigger className="mt-1">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="draft">Draft</SelectItem>
                                <SelectItem value="active">Active</SelectItem>
                                <SelectItem value="archived">Archived</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    
                    <div className="pt-4 border-t border-stone-100">
                        <div className="flex items-center justify-between py-3">
                            <div>
                                <p className="text-sm font-medium text-stone-700">Allow client invites</p>
                                <p className="text-xs text-stone-400">Let invited clients invite others to the room</p>
                            </div>
                            <Switch
                                checked={form.allow_client_invites}
                                onCheckedChange={(v) => setForm(prev => ({ ...prev, allow_client_invites: v }))}
                            />
                        </div>
                        
                        <div className="flex items-center justify-between py-3">
                            <div>
                                <p className="text-sm font-medium text-stone-700">Restrict to company domain</p>
                                <p className="text-xs text-stone-400">Only allow emails from {room.allowed_domain || 'the company domain'}</p>
                            </div>
                            <Switch
                                checked={form.restrict_to_domain}
                                onCheckedChange={(v) => setForm(prev => ({ ...prev, restrict_to_domain: v }))}
                            />
                        </div>
                    </div>
                </div>
                
                <div className="mt-6 pt-4 border-t border-stone-100">
                    <Button onClick={handleSave} disabled={saving} className="bg-stone-900 hover:bg-stone-800">
                        {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </div>
            </div>
        </div>
    );
}

// Main Component
export default function DealRoomDetail() {
    const { roomId } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { getAuthHeader } = useAuth();
    
    const [room, setRoom] = useState(null);
    const [sections, setSections] = useState([]);
    const [invitees, setInvitees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'content');
    
    // Contact selection state
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [contacts, setContacts] = useState([]);
    const [selectedContacts, setSelectedContacts] = useState([]);
    
    // Email invitation state
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [emailTemplate, setEmailTemplate] = useState({ subject: '', body: '' });
    const [selectedInvitees, setSelectedInvitees] = useState([]);
    
    // Preview state
    const [showPreview, setShowPreview] = useState(false);

    useEffect(() => {
        loadRoom();
    }, [roomId]);

    const loadRoom = async () => {
        try {
            const res = await axios.get(`${API}/deal-rooms/${roomId}`, { headers: getAuthHeader() });
            setRoom(res.data);
            setSections(res.data.sections || []);
            setInvitees(res.data.invitees || []);
        } catch (err) {
            toast.error('Failed to load deal room');
            navigate('/deal-rooms');
        } finally {
            setLoading(false);
        }
    };

    const handleSectionCreate = async (data) => {
        try {
            await axios.post(`${API}/deal-rooms/${roomId}/sections`, { ...data, room_id: roomId }, { headers: getAuthHeader() });
            toast.success('Section created');
            loadRoom();
        } catch { toast.error('Failed to create section'); }
    };

    const handleSectionDelete = async (sectionId) => {
        try {
            await axios.delete(`${API}/deal-rooms/${roomId}/sections/${sectionId}`, { headers: getAuthHeader() });
            toast.success('Section deleted');
            loadRoom();
        } catch { toast.error('Failed to delete section'); }
    };

    const handleContentCreate = async (sectionId, data) => {
        try {
            await axios.post(`${API}/deal-rooms/${roomId}/sections/${sectionId}/items`, { ...data, room_id: roomId, section_id: sectionId }, { headers: getAuthHeader() });
            toast.success('Content added');
            loadRoom();
        } catch { toast.error('Failed to add content'); }
    };

    const handleContentDelete = async (itemId) => {
        try {
            await axios.delete(`${API}/deal-rooms/${roomId}/items/${itemId}`, { headers: getAuthHeader() });
            toast.success('Content deleted');
            loadRoom();
        } catch { toast.error('Failed to delete content'); }
    };

    const handleUpdate = async (data) => {
        try {
            await axios.put(`${API}/deal-rooms/${roomId}`, data, { headers: getAuthHeader() });
            loadRoom();
        } catch { toast.error('Failed to update'); }
    };

    const loadContacts = async () => {
        try {
            const params = room.account_id ? `?account_id=${room.account_id}` : '';
            const res = await axios.get(`${API}/contacts${params}`, { headers: getAuthHeader() });
            setContacts(Array.isArray(res.data) ? res.data : (res.data?.data || []));
        } catch {}
    };

    const openInviteModal = () => {
        loadContacts();
        setSelectedContacts([]);
        setShowInviteModal(true);
    };

    const handleAddInvitees = async () => {
        if (selectedContacts.length === 0) return;
        try {
            await axios.post(`${API}/deal-rooms/${roomId}/invitees/from-contacts`, { contact_ids: selectedContacts }, { headers: getAuthHeader() });
            toast.success('Contacts added');
            setShowInviteModal(false);
            loadRoom();
        } catch { toast.error('Failed to add contacts'); }
    };

    const openEmailModal = async () => {
        try {
            const res = await axios.get(`${API}/deal-rooms/${roomId}/email-template`, { headers: getAuthHeader() });
            setEmailTemplate(res.data);
            setSelectedInvitees(invitees.filter(i => i.status === 'pending').map(i => i.invitee_id));
            setShowEmailModal(true);
        } catch { toast.error('Failed to load email template'); }
    };

    const handleSendInvitations = async () => {
        if (selectedInvitees.length === 0) {
            toast.error('Select at least one recipient');
            return;
        }
        try {
            await axios.post(`${API}/deal-rooms/${roomId}/send-invitations`, {
                invitee_ids: selectedInvitees,
                subject: emailTemplate.subject,
                body: emailTemplate.body,
            }, { headers: getAuthHeader() });
            toast.success('Invitations sent');
            setShowEmailModal(false);
            loadRoom();
        } catch { toast.error('Failed to send invitations'); }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-4 border-stone-700 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (!room) return null;

    const TABS = [
        { id: 'content', label: 'Content & Structure', icon: Layers },
        { id: 'analytics', label: `Analytics`, badge: room.total_views, icon: BarChart2 },
        { id: 'settings', label: 'Settings', icon: SettingsIcon },
    ];

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                    <button onClick={() => navigate('/deal-rooms')} className="mt-1 p-2 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-600">
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <div className="flex items-center gap-3">
                            <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                {room.name}
                            </h1>
                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                                room.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 
                                room.status === 'draft' ? 'bg-stone-100 text-stone-600' : 
                                'bg-red-50 text-red-600'
                            }`}>
                                {room.status.charAt(0).toUpperCase() + room.status.slice(1)}
                            </span>
                        </div>
                        {room.account_name && (
                            <p className="text-stone-500 text-sm mt-1 flex items-center gap-1">
                                <Building2 className="w-4 h-4" />
                                {room.account_name}
                            </p>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={() => setShowPreview(true)}>
                        <Eye className="w-4 h-4 mr-2" />
                        Preview
                    </Button>
                    <Button variant="outline" onClick={openInviteModal}>
                        <UserPlus className="w-4 h-4 mr-2" />
                        Add Contacts
                    </Button>
                    <Button onClick={openEmailModal} className="bg-stone-900 hover:bg-stone-800">
                        <Mail className="w-4 h-4 mr-2" />
                        Send Invitations
                    </Button>
                </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-stone-200">
                <nav className="flex gap-6">
                    {TABS.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 py-3 px-1 border-b-2 transition-colors text-sm font-medium ${
                                activeTab === tab.id 
                                    ? 'border-stone-700 text-stone-700' 
                                    : 'border-transparent text-stone-500 hover:text-stone-700 hover:border-stone-300'
                            }`}
                        >
                            <tab.icon className="w-4 h-4" />
                            {tab.label}
                            {tab.badge > 0 && (
                                <span className="text-xs bg-stone-100 text-stone-600 px-1.5 py-0.5 rounded-full">{tab.badge}</span>
                            )}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Tab Content */}
            {activeTab === 'content' && (
                <ContentStructureTab 
                    room={room}
                    sections={sections}
                    onSectionCreate={handleSectionCreate}
                    onSectionDelete={handleSectionDelete}
                    onContentCreate={handleContentCreate}
                    onContentDelete={handleContentDelete}
                    onRefresh={loadRoom}
                />
            )}
            
            {activeTab === 'analytics' && (
                <AnalyticsTab room={room} invitees={invitees} />
            )}
            
            {activeTab === 'settings' && (
                <SettingsTab room={room} onUpdate={handleUpdate} />
            )}

            {/* Invite Contacts Modal */}
            <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Add Contacts from CRM</DialogTitle>
                        <DialogDescription>
                            {room.account_name ? `Select contacts from ${room.account_name}` : 'Select contacts to invite'}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4 max-h-80 overflow-y-auto">
                        {contacts.length === 0 ? (
                            <p className="text-center text-stone-400 py-8">No contacts found</p>
                        ) : (
                            <div className="space-y-2">
                                {contacts.map(contact => {
                                    const isSelected = selectedContacts.includes(contact.contact_id);
                                    const isAlreadyInvited = invitees.some(i => i.email === contact.email);
                                    return (
                                        <button
                                            key={contact.contact_id}
                                            onClick={() => {
                                                if (isAlreadyInvited) return;
                                                setSelectedContacts(prev => 
                                                    isSelected 
                                                        ? prev.filter(id => id !== contact.contact_id)
                                                        : [...prev, contact.contact_id]
                                                );
                                            }}
                                            disabled={isAlreadyInvited}
                                            className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                                                isAlreadyInvited ? 'opacity-50 cursor-not-allowed bg-stone-50 border-stone-100' :
                                                isSelected ? 'border-stone-300 bg-stone-100' : 'border-stone-100 hover:border-stone-200'
                                            }`}
                                        >
                                            <div className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center text-xs font-semibold text-stone-900">
                                                {contact.first_name?.[0]}{contact.last_name?.[0]}
                                            </div>
                                            <div className="flex-1 text-left">
                                                <p className="text-sm font-medium text-stone-800">{contact.first_name} {contact.last_name}</p>
                                                <p className="text-xs text-stone-500">{contact.email}</p>
                                            </div>
                                            {isAlreadyInvited && <span className="text-xs text-stone-400">Already invited</span>}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowInviteModal(false)}>Cancel</Button>
                        <Button 
                            onClick={handleAddInvitees} 
                            disabled={selectedContacts.length === 0}
                            className="bg-stone-900 hover:bg-stone-800"
                        >
                            Add {selectedContacts.length} Contact{selectedContacts.length !== 1 ? 's' : ''}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Email Invitation Modal */}
            <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
                <DialogContent className="sm:max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Send Invitations</DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-6 py-4">
                        {/* Recipients */}
                        <div>
                            <Label className="mb-2 block">Recipients</Label>
                            <div className="border border-stone-200 rounded-lg max-h-60 overflow-y-auto p-2">
                                {invitees.map(invitee => (
                                    <label key={invitee.invitee_id} className="flex items-center gap-2 p-2 hover:bg-stone-50 rounded cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={selectedInvitees.includes(invitee.invitee_id)}
                                            onChange={(e) => {
                                                setSelectedInvitees(prev =>
                                                    e.target.checked
                                                        ? [...prev, invitee.invitee_id]
                                                        : prev.filter(id => id !== invitee.invitee_id)
                                                );
                                            }}
                                            className="rounded border-stone-300"
                                        />
                                        <div>
                                            <p className="text-sm font-medium text-stone-800">{invitee.name}</p>
                                            <p className="text-xs text-stone-500">{invitee.email}</p>
                                        </div>
                                        {invitee.status === 'sent' && (
                                            <span className="text-xs text-blue-600 ml-auto">Sent</span>
                                        )}
                                    </label>
                                ))}
                            </div>
                        </div>
                        
                        {/* Email Editor */}
                        <div>
                            <div className="mb-4">
                                <Label>Subject</Label>
                                <Input
                                    value={emailTemplate.subject}
                                    onChange={(e) => setEmailTemplate(prev => ({ ...prev, subject: e.target.value }))}
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <Label>Email Body</Label>
                                <Textarea
                                    value={emailTemplate.body}
                                    onChange={(e) => setEmailTemplate(prev => ({ ...prev, body: e.target.value }))}
                                    className="mt-1 font-mono text-sm"
                                    rows={10}
                                />
                            </div>
                            <p className="text-xs text-stone-400 mt-2">
                                Variables: {'{recipient_name}'}, {'{sender_name}'}, {'{room_name}'}, {'{access_link}'}, {'{expiration_date}'}
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowEmailModal(false)}>Cancel</Button>
                        <Button onClick={handleSendInvitations} className="bg-stone-900 hover:bg-stone-800">
                            <Mail className="w-4 h-4 mr-2" />
                            Send to {selectedInvitees.length} Recipient{selectedInvitees.length !== 1 ? 's' : ''}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Preview Modal */}
            <Dialog open={showPreview} onOpenChange={setShowPreview}>
                <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Client Preview</DialogTitle>
                        <DialogDescription>This is how the deal room will appear to invited contacts</DialogDescription>
                    </DialogHeader>
                    <div className="border border-stone-200 rounded-xl overflow-hidden mt-4">
                        {/* Preview Header */}
                        <div className="bg-gradient-to-r from-stone-900 to-stone-700 p-6 text-white">
                            <h2 className="text-xl font-bold">{room.name}</h2>
                            {room.account_name && <p className="opacity-80 text-sm mt-1">{room.account_name}</p>}
                        </div>
                        
                        {/* Preview Content */}
                        <div className="p-6 bg-stone-50">
                            {sections.length === 0 ? (
                                <p className="text-center text-stone-400 py-8">No content yet</p>
                            ) : (
                                <div className="space-y-6">
                                    {sections.map(section => (
                                        <div key={section.section_id} className="bg-white rounded-xl p-4 border border-stone-200">
                                            <h3 className="font-semibold text-stone-900 mb-2">{section.name}</h3>
                                            {section.description && <p className="text-sm text-stone-500 mb-4">{section.description}</p>}
                                            {section.items && section.items.length > 0 ? (
                                                <div className="space-y-2">
                                                    {section.items.map(item => {
                                                        const IconComponent = CONTENT_TYPE_ICONS[item.content_type] || FileText;
                                                        return (
                                                            <div key={item.item_id} className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg">
                                                                <IconComponent className="w-5 h-5 text-stone-700" />
                                                                <div>
                                                                    <p className="text-sm font-medium text-stone-800">{item.title}</p>
                                                                    {item.file_name && (
                                                                        <p className="text-xs text-stone-500">{item.file_name} • {formatFileSize(item.file_size)}</p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <p className="text-sm text-stone-400">No items in this section</p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
